import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { FilterKeyType } from '@ocean/types/dist/filter'
import { ApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { getVirtualEvent } from 'modules/virtualClassrooms/common/api/managerService'
import { getListVirtualEventSchedules } from 'modules/virtualClassrooms/manager/Schedules/api/schedulesService'
import { VirtualEventSchedule } from 'modules/virtualClassrooms/manager/Schedules/types/list'
import {
  ShowType,
  ScheduleStatus,
  SchedulesFilterItem,
  SchedulesFilterItems,
} from 'modules/virtualClassrooms/manager/Schedules/types/list'
import { ScheduleListUtils } from 'modules/virtualClassrooms/manager/Schedules/utils'
import {
  ListState,
  ApiVirtualEventDetailsResponse,
  ApiListVirtualEventSchedulesResponse,
} from './types'

const initialState: ListState = {
  eventDetailsData: {},
  scheduleList: {},
  scheduleListFilters: {
    status: { keyType: FilterKeyType.Number, value: ScheduleStatus.Upcoming },
    showType: { keyType: FilterKeyType.Number, value: ShowType.Own },
  },
}

const schedulesListSlice = createSlice({
  name: 'virtualClassroomManagerSchedulesList',
  initialState,
  reducers: {
    eventDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiVirtualEventDetailsResponse>>
    ) {
      state.eventDetailsData = action.payload
    },
    clearEventDetails(state) {
      state.eventDetailsData = initialState.eventDetailsData
    },
    listVirtualEventSchedulesLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventSchedulesResponse>>
    ) {
      state.scheduleList = action.payload
    },
    setScheduleListFilters(state, action: PayloadAction<SchedulesFilterItem>) {
      const filter = state.scheduleListFilters[action.payload.name]
      if (filter) filter.value = action.payload.value
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { eventDetailsLoaded, listVirtualEventSchedulesLoaded } = schedulesListSlice.actions
export const { setScheduleListFilters, clearEventDetails } = schedulesListSlice.actions

export default schedulesListSlice.reducer

export const loadEventDetailsData = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getVirtualEvent(eventId),
    },
    payloadAction: eventDetailsLoaded,
  })
}

export const getFilteredScheduleList = createSelector(
  (schedules: VirtualEventSchedule[] = [], filters: SchedulesFilterItems) => ({
    schedules,
    filters,
  }),
  ({ schedules, filters }) => ScheduleListUtils.getFilteredSchedules(schedules, filters)
)

export const loadListVirtualEventSchedules = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventSchedules(eventId),
    },
    payloadAction: listVirtualEventSchedulesLoaded,
  })
}
